import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { addEntities, removeAllEntities, setAllEntities, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { IrisDMSFolderOrFile } from '@iris/modules/dms/utils/file.utils';
import { IrisGridPaginationI } from '@iris/common/modules/grid/ag-grid/models/grid-pagination';
import { AbstractDatetime } from '@iris/common/modules/date';
import { computed, inject, Signal } from '@angular/core';
import { IrisDMSFolder, IrisDMSFolderI } from '../models/IrisDMSFolder';
import { IrisDMSFile, IrisDMSFileI } from '../models/IrisDMSFile';
import { IrisTrashBinService } from '../services/trash-bin.service';
import { SortModel } from '@iris/common/models/SortModel';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { IrisQueryParams, IrisQueryParamsBuilder, IrisQueryParamsOrderDirection } from '@iris/api-query';
import { isNil } from 'lodash';
import { catchError, debounceTime, distinctUntilChanged, filter, map, of, pipe, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import { TranslateService } from '@ngx-translate/core';
import { setError, setLoaded, setLoading, withRequestState } from '@iris/common/signals/features/request-status.feature';
import { IrisContentViewType } from '../models/IrisDmsViewType';

export interface DmsTrashBinFilter {
  originalName: string;
  removedBy: number;
  removedDateFrom: AbstractDatetime;
  removedDateTo: AbstractDatetime;
}

export interface DmsTrashBinState {
  checkAll: boolean;
  pagination: IrisGridPaginationI;
  filter: DmsTrashBinFilter;
  selectedIds: string[];
  unselectedIds: string[];
  contentViewType: IrisContentViewType;
}

const defaultPagination: IrisGridPaginationI = {
  offset: 0,
  count: 0,
  limit: 25,
};

const initialState: DmsTrashBinState = {
  checkAll: false,
  pagination: defaultPagination,
  filter: {
    originalName: '',
    removedBy: null,
    removedDateFrom: null,
    removedDateTo: null,
  },
  selectedIds: [],
  unselectedIds: [],
  contentViewType: IrisContentViewType?.List,
};

export const IrisDmsTrashBinStore = signalStore(
  { providedIn: 'root', protectedState: false },
  withEntities<IrisDMSFolderOrFile>(),
  withState<DmsTrashBinState>(initialState),
  withRequestState(),
  withComputed(({ entities, pagination, checkAll, unselectedIds, selectedIds, contentViewType }) => ({
    currentPage: computed(() => entities().slice(pagination.offset(), pagination.offset() + pagination.limit())),
    paginationCount: computed(() => pagination.count()),
    selectedRowsCount: computed(() => {
      const unselectedIdsLength = unselectedIds().length;
      const selectedIdsLength = selectedIds().length;
      return checkAll() ? pagination.count() - unselectedIdsLength : selectedIdsLength;
    }),
    hasAnySelection: computed(() => selectedIds().length > 0),
    hasNextPage: computed(() => pagination.offset() + pagination.limit() < pagination.count()),
    page: computed(() => pagination.offset() / pagination.limit()),
    tileViewActivated: computed(() => contentViewType() === IrisContentViewType.Tile),
  })),
  withMethods((
    store,
    trashBinService = inject(IrisTrashBinService),
    globalSandbox = inject(IrisGlobalSandbox),
  ) => ({
    getFolderById(folderId: string): Signal<IrisDMSFolderI> {
      return computed(() => store.entityMap()[folderId] as IrisDMSFolderI);
    },
    getFileById(fileId: string): IrisDMSFileI {
      return store.entityMap()[fileId] as IrisDMSFileI;
    },
    reset(): void {
      patchState(store, removeAllEntities(), initialState);
    },
    setContentViewType(viewType: IrisContentViewType): void {
      patchState(store, { contentViewType: viewType || IrisContentViewType.List });
    },
    loadEntities$: rxMethod<{ offset: number; sortModel?: SortModel }>(
      pipe(
        distinctUntilChanged(),
        filter(() => !store.loading()),
        withLatestFrom(globalSandbox.currentProjectId$),
        switchMap(([{ offset, sortModel } ,projectId]) => {
          patchState(store, setLoading());
          const filter = store.filter;
          const filterQuery = getFilterQueryParams(filter.originalName(), filter.removedBy(), filter.removedDateFrom(), filter.removedDateTo());
          const query = buildQuery(filterQuery, offset, store.pagination.limit(), sortModel);
          return trashBinService.getEntities(projectId, query).pipe(
            take(1),
            map(({ files, folders, count }) => {
              const entities = [...folders.map(folder => new IrisDMSFolder(folder)), ...files.map(file => new IrisDMSFile(file))];

              const entityAction = store.contentViewType() === IrisContentViewType.Tile && offset > 0 ? addEntities : setAllEntities;
              const selectedIds = store.selectedIds();
              if (store.checkAll()) {
                entities.forEach(entity => {
                  if (![...selectedIds, ...store.unselectedIds()].some(_id => _id === entity.id)) {
                    selectedIds.push(entity.id);
                  }
                });
              }

              patchState(store, entityAction(entities));
              patchState(store, { pagination: { ...store.pagination(), count, offset }, selectedIds }, setLoaded());
              return entities;
            }),
            catchError(err => {
              patchState(store, setError(err));
              return of(err);
            }),
          );
        }),
      ),
    ),
    setSelectedIds(selectedIds: string[]): void {
      patchState(store, { selectedIds });
    },
    setUnselectedIds(unselectedIds: string[]): void {
      patchState(store, { unselectedIds });
    },
    setFilter(originalName: string, removedBy: number, removedDateFrom: AbstractDatetime, removedDateTo: AbstractDatetime): void {
      patchState(store, { filter: { originalName, removedBy, removedDateFrom, removedDateTo } });
      this.loadPage(0);
    },
    setPageSize(limit: number): void {
      patchState(store, { pagination: { limit, offset: 0 } });
    },
    setCheckAll(checkAll: boolean): void {
      patchState(store, {
        checkAll,
        selectedIds: checkAll ? store.entities().map(entity => entity.id) : [],
        unselectedIds: [],
      });
    },
    selectCurrentPage(): void {
      patchState(store, { checkAll: false, selectedIds: store.entities().map(file => file.id), unselectedIds: [] });
    },
    select(idToSelect: string): void {
      const selectedIds = store.selectedIds();
      if (selectedIds.includes(idToSelect)) { return; }
      patchState(store, {
        selectedIds: [...selectedIds, idToSelect],
        unselectedIds: store.unselectedIds().filter(id => id !== idToSelect),
      });
    },
    deselect(idToDeselect: string): void {
      const selectedIds = store.selectedIds();
      if (!selectedIds.includes(idToDeselect)) { return; }
      const unselectedIds = store.unselectedIds();
      if (!unselectedIds.includes(idToDeselect) && store.checkAll()) {
        unselectedIds.push(idToDeselect);
      }
      patchState(store, { unselectedIds, selectedIds: selectedIds.filter(id => id !== idToDeselect) });
    },
    removeAll(): void {
      patchState(store, removeAllEntities());
    },
  })),
  withMethods((store) => ({
    loadPage(page: number, sortModel?: SortModel): void {
      store.loadEntities$({ offset: store.pagination.limit() * page, sortModel });
    },
  })),
  withMethods((
    store,
    trashBinService = inject(IrisTrashBinService),
    globalSandbox = inject(IrisGlobalSandbox),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) => ({
    removeSelectedEntities$: rxMethod<void>(
      pipe(
        switchMap(() => globalSandbox.currentProjectId$.pipe(take(1))),
        switchMap(projectId => {
          const { originalName, removedBy, removedDateFrom, removedDateTo } = store.filter;
          const filterQuery = getFilterQueryParams(originalName(), removedBy(), removedDateFrom(), removedDateTo());
          const params = getBulkActionParams(store.checkAll(), store.selectedIds(), store.unselectedIds());
          return trashBinService.removeBulk(projectId, ...params, filterQuery).pipe(
            map(removedEntities => {
              const { offset, limit, count } = store.pagination;
              const newOffset = (offset() + removedEntities.length) === count() ? (offset() - limit()) : offset();
              toastService.success({ message: translateService.instant('text.dms.FolderAndFilesDeletedInfo') });
              patchState(store, {
                selectedIds: [],
                unselectedIds: [],
                pagination: { ...store.pagination(), offset: Math.max(newOffset, 0) },
              });
              store.loadPage(0);
              return removedEntities;
            }),
            catchError(() => of([])),
          );
        }),
      ),
    ),
    restoreSelectedEntities$: rxMethod<string | void>(
      pipe(
        withLatestFrom(globalSandbox.currentProjectId$),
        switchMap(([entityId, projectId]) => {
          const { originalName, removedBy, removedDateFrom, removedDateTo } = store.filter;
          const filterQuery = getFilterQueryParams(originalName(), removedBy(), removedDateFrom(), removedDateTo());
          let params = getBulkActionParams(store.checkAll(), store.selectedIds(), store.unselectedIds());
          if (entityId) {
            params = getBulkActionParams(false, [entityId], undefined);
          }
          return trashBinService.restoreBulk(projectId, ...params, filterQuery).pipe(
            switchMap((entities) => {
              const { offset, limit, count } = store.pagination;
              const newOffset = (offset() + entities.length) === count() ? (offset() - limit()) : offset();
              patchState(store, {
                selectedIds: [],
                unselectedIds: [],
                pagination: { ...store.pagination(), offset: Math.max(newOffset, 0) },
              });
              store.loadPage(0);
              return entities;
            }),
          );
        }),
      ),
    ),
    trashBinChanged$: rxMethod<string>(
      pipe(
        debounceTime(5000),
        tap(() => store.loadPage(0)),
      ),
    ),
  })),
);

function buildQuery(params: IrisQueryParams, offset: number, limit: number, sortModel?: SortModel): IrisQueryParams {
  const queryBuilder: IrisQueryParamsBuilder = new IrisQueryParamsBuilder(params.toObject())
    .offset(offset)
    .limit(limit);

  if(!isNil(sortModel)) {
    queryBuilder.orderBy(sortModel.value, sortModel.order as IrisQueryParamsOrderDirection);
  }

  return queryBuilder.toStructure();
}

function getFilterQueryParams(originalName: string, removedBy: number, removedDateFrom: AbstractDatetime, removedDateTo: AbstractDatetime): IrisQueryParams {
  const queryBuilder = new IrisQueryParamsBuilder();
  if (originalName) {
    queryBuilder.filter('originalName', [originalName], builderFn => builderFn.method('fts'));
  }

  if (removedBy) {
    queryBuilder.filter('removedBy', [removedBy], builderFn => builderFn.method('eq'));
  }

  if (removedDateFrom || removedDateTo) {
    queryBuilder.filter('removedDate', [removedDateFrom, removedDateTo], builderFn => builderFn.method('btw').strict(false));
  }

  return queryBuilder.toStructure();
}

function getBulkActionParams(checkAll, selectedIds, unselectedIds): [string[], string[]] {
  return checkAll ? [undefined, unselectedIds] : [selectedIds, undefined];
}
